import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Button } from "../ui/button";
import { ArrowRight, Calendar as CalendarIcon } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * Inline row-action popover for the Daily To-Do table.
 * Lets the user reschedule an incomplete entry to tomorrow or any future date.
 * If the user just clicks Move without picking a date, defaults to tomorrow.
 */
export default function MoveDailyMenu({ row, onMoved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const tomorrowIso = () => {
    const d = new Date(row.date || new Date().toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };
  const [target, setTarget] = useState("");

  const move = async (dateStr) => {
    if (!row?.id) return;
    setBusy(true);
    try {
      const newDate = dateStr || tomorrowIso();
      await api.put(`/ops/daily/${row.id}`, { ...row, date: newDate });
      toast.success(`Moved to ${newDate}`);
      setOpen(false);
      setTarget("");
      onMoved && onMoved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--rw-border)] rounded-[4px] bg-white hover:bg-[var(--rw-surface-2)]"
          data-testid={`move-row-${row.id}`}
        >
          <ArrowRight size={12} /> Move
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 bg-white p-3 z-[80]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs uppercase tracking-[0.08em] font-semibold text-neutral-500 mb-2">
          Reschedule this task
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start mb-2"
          onClick={() => move(tomorrowIso())}
          disabled={busy}
          data-testid="move-tomorrow"
        >
          <ArrowRight size={12} className="mr-2" /> Move to tomorrow
        </Button>
        <label className="text-xs text-neutral-600 flex items-center gap-1 mb-1">
          <CalendarIcon size={12} /> Or pick a date:
        </label>
        <input
          type="date"
          value={target}
          min={tomorrowIso()}
          onChange={(e) => setTarget(e.target.value)}
          className="w-full text-sm border border-[var(--rw-border)] rounded-[4px] px-2 py-1 mb-2"
          data-testid="move-date-input"
        />
        <Button
          size="sm"
          className="w-full bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]"
          onClick={() => move(target)}
          disabled={busy}
          data-testid="move-confirm"
        >
          {busy ? "Moving…" : target ? "Move to date" : "Move (default: tomorrow)"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
